import { Box, Button, Card, CardContent, Paper, Typography } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useNavigationInterceptorContext } from "../../../contexts/useNavigationInterceptorContext";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import {
	usePatchMySkillsFreelancerOnboardingStep,
	useSaveMySkillsFreelancerOnboardingProgress
} from "../../../hooks/useFreelancerProfile";
import {
	useDataProcessStages,
	useExpertiseExperiences
} from "../../../hooks/useReferential";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";
import { zoullooColors } from "../../../theme";
import {
	FreelancerOnboardingDataProcessStageViewModel,
	FreelancerOnboardingLanguageViewModel,
	FreelancerOnboardingMySkillsStepViewModel,
	FreelancerOnboardingRegionViewModel,
	FreelancerOnboardingSkillViewModel,
	FreelancerOnboardingToolViewModel
} from "../../../ViewModels/FreelancerOnboardingMySkillsStepViewModel";
import FreelancerOnboardingMySkillsDataProcessStagesBloc from "./components/skills/FreelancerOnboardingMySkillsDataProcessStagesBloc";
import FreelancerOnboardingMySkillsLanguagesBloc from "./components/skills/FreelancerOnboardingMySkillsLanguagesBloc";
import FreelancerOnboardingMySkillsRegionsBloc from "./components/skills/FreelancerOnboardingMySkillsRegionsBloc";
import FreelancerOnboardingMySkillsSkeleton from "./components/skills/FreelancerOnboardingMySkillsSkeleton";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";

const FreelancerOnboardingMySkills: React.FC = () => {
	const { data: expertiseExperiences } = useExpertiseExperiences();
	const {
		setCurrentOnboardingFormStep,
		setNextOnboardingFormStep: setNextOnboardingStep,
		setFreelancerOnboardingMySkillsStepViewModel,
		freelancerQueryResult,
		freelancerProfileQueryResult
	} = useFreelancerContext();
	const { data: freelancer } = freelancerQueryResult;
	const { data: freelancerProfile, isLoading: isFreelancerProfileLoading } =
		freelancerProfileQueryResult;
	const { data: dataProcessStages, isLoading: isDataProcessStagesLoading } =
		useDataProcessStages(
			expertiseExperiences?.filter(
				x =>
					x.expertise.id === freelancerProfile?.expertise?.id &&
					x.experience.id === freelancerProfile?.experience?.id
			)?.[0]?.id
		);
	const patchMySkillsFreelancerOnboardingStep =
		usePatchMySkillsFreelancerOnboardingStep();
	const saveMySkillsFreelancerOnboardingProgression =
		useSaveMySkillsFreelancerOnboardingProgress({ hideAllSnackbars: true });
	const { interceptNavigation } = useNavigationInterceptorContext();
	const { t } = useTranslation();

	const validationSchema = yup.object({
		languageViewModels: yup
			.array()
			.min(1, t("FreelancerOnboardingMySkills.validation.atLeastOneLanguageRequired"))
			.test(
				"unique-languages",
				t("FreelancerOnboardingMySkills.validation.duplicateLanguagesNotAllowed"),
				languages => {
					if (!languages) return true;
					const languageIds = languages
						.map((lang: FreelancerOnboardingLanguageViewModel) => lang.language?.id)
						.filter((id): id is number => id !== undefined);
					return languageIds.length === new Set(languageIds).size;
				}
			),
		regionViewModels: yup
			.array()
			.min(1, t("FreelancerOnboardingMySkills.validation.atLeastOneRegionRequired"))
	});
	const initialValues: FreelancerOnboardingMySkillsStepViewModel = {
		dataProcessStageViewModels:
			dataProcessStages?.map(
				dataProcessStage =>
					({
						id: dataProcessStage.id,
						skillViewModels:
							freelancerProfile?.skills
								.filter(x => x.dataProcessStage.id === dataProcessStage.id)
								.map(
									x =>
										({
											id: x.id,
											skill: x.skill,
											dataProcessStage: x.dataProcessStage,
											level: x.level
										} as FreelancerOnboardingSkillViewModel)
								) ?? [],
						toolViewModels:
							freelancerProfile?.tools
								.filter(x => x.dataProcessStage.id === dataProcessStage.id)
								.map(
									x =>
										({
											id: x.id,
											tool: x.tool,
											dataProcessStage: x.dataProcessStage,
											level: x.level
										} as FreelancerOnboardingToolViewModel)
								) ?? []
					} as FreelancerOnboardingDataProcessStageViewModel)
			) ?? [],
		languageViewModels:
			freelancer?.languages.map(
				x =>
					({
						id: x.id,
						language: x.language,
						languageProficiency: x.languageProficiency
					} as FreelancerOnboardingLanguageViewModel)
			) ?? [],
		regionViewModels:
			freelancerProfile?.regions.map(
				x =>
					({
						id: x.id,
						region: { id: x.region.id, label: x.region.label }
					} as FreelancerOnboardingRegionViewModel)
			) ?? [],
		form: null
	};

	const formik = useFormik<FreelancerOnboardingMySkillsStepViewModel>({
		initialValues,
		validationSchema,
		onSubmit: async (values: FreelancerOnboardingMySkillsStepViewModel) => {
			try {
				await patchMySkillsFreelancerOnboardingStep.mutateAsync({
					freelancer: freelancer!,
					freelancerProfile: freelancerProfile!,
					freelancerOnboardingMySkillsStepViewModel: values,
					newStep:
						freelancerProfile?.onboarding.currentStatus ===
						FreelancerOnboardingStatus.Accepted
							? FreelancerOnboardingFormStep.MySkills
							: FreelancerOnboardingFormStep.MySectors
				});

				if (
					freelancerProfile?.onboarding.currentStatus !==
					FreelancerOnboardingStatus.Accepted
				) {
					setCurrentOnboardingFormStep(FreelancerOnboardingFormStep.MySectors);
				}
			} catch (error) {
				console.error("Error submitting form:", error);
				formik.setFieldValue("form.isSubmitting", false);
			}
		},
		enableReinitialize: true
	});

	useEffect(() => {
		setFreelancerOnboardingMySkillsStepViewModel({
			...formik.values,
			form: {
				isValid: validationSchema.isValidSync(formik.values),
				isSubmitting: formik.isSubmitting,
				isDirty: formik.dirty
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values]);

	if (isFreelancerProfileLoading || !freelancerProfile) {
		return <></>;
	}

	return (
		<Card
			component={Paper}
			elevation={0}
			sx={{
				margin: "32px"
			}}
		>
			<CardContent>
				<Typography variant="h2" component="h1" sx={{ mb: 3, fontWeight: "bold" }}>
					{t("FreelancerOnboardingMySkills.title")}
				</Typography>
				<Typography variant="body1" marginBottom={"40px"}>
					{t("FreelancerOnboardingMySkills.description")}
				</Typography>

				{isDataProcessStagesLoading && <FreelancerOnboardingMySkillsSkeleton />}
				<form onSubmit={formik.handleSubmit}>
					{dataProcessStages?.map((dataProcessStage, index) => (
						<FreelancerOnboardingMySkillsDataProcessStagesBloc
							dataProcessStage={dataProcessStage}
							dataProcessStageIndex={index}
							formik={formik}
							key={`dataProcessStage_${dataProcessStage.id}`}
						/>
					))}

					<FreelancerOnboardingMySkillsLanguagesBloc formik={formik} />

					<FreelancerOnboardingMySkillsRegionsBloc formik={formik} />

					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							gap: 3,
							mt: 2
						}}
					>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							disabled={formik.isSubmitting}
							sx={{
								color: zoullooColors.secondary.contrastText,
								width: "40%"
							}}
						>
							{freelancerProfile.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted
								? t("FreelancerOnboardingMySkills.buttons.nextStep")
								: t("common.save")}
						</Button>
						{freelancerProfile.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted && (
							<Button
								variant="outlined"
								color="secondary"
								sx={{
									width: "40%"
								}}
								onClick={() => {
									interceptNavigation({
										onValidNavigation: async () => {
											try {
												await saveMySkillsFreelancerOnboardingProgression.mutateAsync({
													freelancer: freelancer!,
													freelancerProfile,
													freelancerOnboardingMySkillsStepViewModel: formik.values,
													newStep: FreelancerOnboardingFormStep.MyExpertise
												});

												setCurrentOnboardingFormStep(
													FreelancerOnboardingFormStep.MyExpertise
												);
											} catch (error) {
												console.error(
													"Error returning back to the previous step:",
													error
												);
											}
										},
										onBlockedNavigation: () => {
											setNextOnboardingStep(FreelancerOnboardingFormStep.MyExpertise);
										}
									});
								}}
							>
								{t("FreelancerOnboardingMySkills.buttons.back")}
							</Button>
						)}
					</Box>
				</form>
			</CardContent>
		</Card>
	);
};

export default FreelancerOnboardingMySkills;
